// /app/frontend/src/components/chat/ComposerAssist.jsx
/**
 * Composer Assist - P0 Loop #2
 * 
 * A popover menu that helps users continue conversations by inserting editable text.
 * 
 * Options:
 * 1. Send today's thought (shares Inner Work prompt + ask)
 * 2. Ask a deeper question (curated, poly-safe, rotating)
 * 3. Continue this conversation (soft follow-ups)
 * 4. Reflect privately (opens Inner Work modal - won't send)
 * 
 * Key behavior:
 * - Never auto-send - only inserts editable text into the input box
 * - No modal except "Reflect privately"
 * - ESC or click outside closes menu
 */

import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, Send, HelpCircle, MessageCircle, BookOpen } from "lucide-react";
import { getTodayPrompt } from "../../api/innerWork";

// Curated deeper questions - human, poly-safe, not trauma-bait
const DEEP_QUESTIONS = [
  "What's something you value in relationships that you learned the hard way?",
  "What does commitment mean to you in this season of life?",
  "How do you like to handle jealousy when it shows up?",
  "What boundary are you proud of learning?",
  "What does 'peace' feel like in a relationship for you?",
];

// Soft follow-ups for continuing conversations
const CONTINUE_PROMPTS = [
  "Tell me more about that—what made it stand out to you?",
  "That makes sense. What led you to that?",
  "I like that. How did you learn that about yourself?",
  "What's something you wish people understood about your relationship style?",
];

// Share the prompt starter
const SHARE_PROMPT_TEXT = "Quick vibe check: what kind of connection are you open to exploring right now—light, deep, or somewhere in between?";

// Fallback when no prompt available
const FALLBACK_STARTER = SHARE_PROMPT_TEXT;

// Random picker
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function ComposerAssist({
  onInsertText,
  onOpenInnerWork,
  messageCount = 0,
  lastMessageTime = null,
  matchId,
  userId,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [todayPrompt, setTodayPrompt] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Compute menu position when opening (portal-based, viewport-clamped)
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const computePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 288; // w-72 = 18rem = 288px
      const menuHeight = 280; // approximate
      const padding = 8;
      const vw = window.visualViewport?.width || window.innerWidth;
      const vh = window.visualViewport?.height || window.innerHeight;

      // Position above the button, left-aligned
      let top = rect.top - menuHeight - 8;
      let left = rect.left;

      // Clamp to viewport
      if (top < padding) {
        // Show below if no room above
        top = rect.bottom + 8;
      }
      if (left + menuWidth > vw - padding) {
        left = vw - menuWidth - padding;
      }
      if (left < padding) {
        left = padding;
      }

      setMenuPosition({ top, left });
    };

    computePosition();

    // Recompute on viewport changes
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', computePosition);
      vv.addEventListener('scroll', computePosition);
    }
    window.addEventListener('scroll', computePosition, { passive: true });

    return () => {
      if (vv) {
        vv.removeEventListener('resize', computePosition);
        vv.removeEventListener('scroll', computePosition);
      }
      window.removeEventListener('scroll', computePosition);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // Load today's prompt when menu opens
  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    (async () => {
      try {
        const prompt = await getTodayPrompt();
        if (mounted) setTodayPrompt(prompt);
      } catch {
        // Silent fail - fallback will be used
      }
    })();

    return () => { mounted = false; };
  }, [isOpen]);

  // Build menu items
  const menuItems = useMemo(() => {
    // "Share the topic" - always use curated poly-safe text
    const promptText = SHARE_PROMPT_TEXT;

    return [
      {
        key: "share_prompt",
        icon: Send,
        label: "Share the topic",
        sublabel: "Editable vibe check — never auto-sends",
        action: () => {
          console.log('[ComposerAssist] share_prompt clicked, inserting text');
          onInsertText?.(promptText);
          setIsOpen(false);
        },
        color: "purple",
      },
      {
        key: "ask_deeper",
        icon: HelpCircle,
        label: "Ask a deeper question",
        sublabel: "Curated poly-safe question",
        action: () => {
          console.log('[ComposerAssist] ask_deeper clicked');
          onInsertText?.(pick(DEEP_QUESTIONS));
          setIsOpen(false);
        },
        color: "amber",
      },
      {
        key: "continue",
        icon: MessageCircle,
        label: "Continue the conversation",
        sublabel: "Soft follow-up",
        action: () => {
          console.log('[ComposerAssist] continue clicked');
          onInsertText?.(pick(CONTINUE_PROMPTS));
          setIsOpen(false);
        },
        color: "blue",
      },
      {
        key: "reflect",
        icon: BookOpen,
        label: "Reflect privately",
        sublabel: "Opens Inner Work • Won't send anything",
        action: () => {
          console.log('[ComposerAssist] reflect clicked, opening inner work');
          onOpenInnerWork?.();
          setIsOpen(false);
        },
        color: "slate",
        special: true, // Different styling
      },
    ];
  }, [onInsertText, onOpenInnerWork]);

  const colorClasses = {
    purple: "bg-purple-50 hover:bg-purple-100 text-purple-600",
    amber: "bg-amber-50 hover:bg-amber-100 text-amber-600",
    blue: "bg-blue-50 hover:bg-blue-100 text-blue-600",
    slate: "bg-slate-900 hover:bg-slate-800 text-white",
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Assist Button - touch-manipulation prevents iOS pan gestures */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition ${
          isOpen
            ? "bg-purple-600 text-white"
            : "bg-purple-100 hover:bg-purple-200 text-purple-600"
        }`}
        style={{ touchAction: 'manipulation' }}
        title="Composer Assist"
        aria-label="Open composer assist menu"
        data-testid="composer-assist-btn"
      >
        {isOpen ? (
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        ) : (
          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        )}
      </button>

      {/* Popover Menu - Portal-based for stable positioning */}
      {isOpen && createPortal(
        <>
          {/* Backdrop - transparent click catcher, z-index below menu */}
          <div
            className="fixed inset-0"
            style={{ 
              zIndex: 9998,
              background: 'transparent',
              pointerEvents: 'auto',
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('[ComposerAssist] Backdrop clicked, closing menu');
              setIsOpen(false);
            }}
          />
          {/* Menu - must have higher z-index than backdrop */}
          <div
            className="fixed w-72 surface-card rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
            style={{
              zIndex: 9999,
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()} // Prevent backdrop from catching menu clicks
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Composer Assist</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Inserts editable text. Nothing auto-sends.
              </p>
            </div>

            {/* Menu Items */}
            <div className="p-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`w-full text-left p-3 rounded-xl mb-1 last:mb-0 transition ${
                      item.special
                        ? colorClasses[item.color]
                        : `hover:bg-gray-50 text-gray-800`
                    }`}
                    onClick={item.action}
                    data-testid={`composer-assist-${item.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        item.special
                          ? "bg-white/10"
                          : colorClasses[item.color]
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${
                          item.special ? "text-white" : "text-gray-900"
                        }`}>
                          {item.label}
                        </div>
                        <div className={`text-[11px] ${
                          item.special ? "text-white/70" : "text-gray-500"
                        }`}>
                          {item.sublabel}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
